/**
 * Skeleton loaders for structured content.
 *
 * `UI_GUIDELINES.md` §3.9: a table or card grid loads behind a skeleton that
 * mirrors the eventual layout, not a centered spinner — "a centered spinner
 * over a structured page reads as a stall and hides the shape of what's
 * loading". A spinner is correct only for a single-item detail page where there
 * is no meaningful shape to draw.
 *
 * `aria-hidden` with a sibling live region rather than markup a screen reader
 * walks: reading out eight rows of placeholder cells is worse than silence, so
 * the shimmer is decorative and the announcement is a single "Loading …".
 */

/**
 * Duplicated in both dashboards, deliberately — and the distinction from
 * vista#671 matters, because that PR just removed two copies of the fetch
 * client for looking like this.
 *
 * What #671 consolidated was **behaviour**: a bugfix landed on one copy and not
 * the other, twice, and the divergence was invisible until something broke.
 * This file is presentation with no behaviour and no bugfix surface — if the
 * two copies drift visually, nothing misbehaves. The alternative is JSX in
 * `@vista-cloud/web-common`, which would cost a build step or a transform
 * config in both apps for markup neither app can get wrong.
 *
 * Anything with a decision in it goes in the shared package instead. That is
 * why the time FORMATTING lives there and `<RelativeTime>` does not.
 */

function Bar({ className = '' }) {
  return <div className={`h-3 rounded bg-gray-200 ${className}`} />;
}

/**
 * A table's shape while it loads: the header row, then `rows` × `cols` cells.
 *
 * Column widths vary deliberately — uniform bars read as a rendering bug
 * rather than as content arriving.
 */
export function TableSkeleton({ rows = 6, cols = 4, message = 'Loading…' }) {
  const widths = ['w-2/3', 'w-1/2', 'w-3/4', 'w-1/3', 'w-5/6', 'w-2/5'];
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <span className="sr-only" role="status">
        {message}
      </span>
      <div aria-hidden="true" className="animate-pulse">
        <div className="flex gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3">
          {Array.from({ length: cols }, (_, c) => (
            <Bar key={c} className={`${widths[c % widths.length]} flex-1 bg-gray-300`} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex gap-4 border-b border-gray-100 px-4 py-3 last:border-0">
            {Array.from({ length: cols }, (_, c) => (
              <Bar key={c} className={`${widths[(r + c) % widths.length]} flex-1`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A card grid's shape while it loads. */
export function CardSkeleton({ count = 3, message = 'Loading…' }) {
  return (
    <div className="space-y-4">
      <span className="sr-only" role="status">
        {message}
      </span>
      <div aria-hidden="true" className="animate-pulse space-y-4">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
            <Bar className="w-1/3 bg-gray-300" />
            <div className="mt-3 space-y-2">
              <Bar className="w-full" />
              <Bar className="w-4/5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A form's shape while it loads — label/field pairs, not a spinner. */
export function FormSkeleton({ fields = 4, message = 'Loading…' }) {
  return (
    <div className="max-w-md space-y-4 rounded-lg border border-gray-200 bg-white p-6">
      <span className="sr-only" role="status">
        {message}
      </span>
      <div aria-hidden="true" className="animate-pulse space-y-5">
        {Array.from({ length: fields }, (_, i) => (
          <div key={i} className="space-y-2">
            <Bar className="w-1/4" />
            <div className="h-9 rounded-md border border-gray-200 bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  );
}
